import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { UptimeBarsPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/msp')({
  component: MspPage,
  head: () => ({
    meta: [
      { title: 'MSP Website Monitoring — All Clients, One Place' },
      { name: 'description', content: 'MSP monitoring: white-label status pages, per-client team roles, API automation, escalation policies. From $3/mo.' },
      { property: 'og:title', content: 'MSP Website Monitoring — All Clients, One Place | Uptrack' },
      { property: 'og:description', content: 'Monitor all your clients from one dashboard. White-label status pages, team roles, API automation. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-msp.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/msp' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For MSPs', item: 'https://uptrack.app/for/msp' },
          ],
        }),
      },
    ],
  }),
})

function MspPage() {
  return (
    <FeaturePage
      section="MSP website monitoring"
      eyebrow="For · MSP website monitoring"
      title="Monitor all your clients from one place."
      intro='1,000 monitors on Business. White-label status pages for each client. Team roles to assign technicians per account. API for automation. Everything an MSP needs to deliver reliable monitoring at scale.'
      featuresHeading="Built for managed service providers"
      features={[
        { title: '1,000 monitors on Business', desc: 'Scale monitoring across your entire client base. No per-monitor pricing surprises.' },
        { title: 'White-label status pages', desc: 'Branded status pages for each client with their own domain. Your clients see your brand, not ours.' },
        { title: 'Team roles per client', desc: 'Assign technicians to specific client accounts. Control who sees what with role-based access.' },
        { title: 'API for automation', desc: 'Provision monitors automatically when onboarding new clients. Full REST API on every paid plan.' },
        { title: 'Escalation policies', desc: 'First alert goes to the on-call tech. No response? Escalate to the team lead. Multi-tier alerting.' },
        { title: 'Custom email sender', desc: 'Alert emails come from your domain. Clients see your brand in every notification.' },
      ]}
      why={{ heading: 'Scale your monitoring with your client base', body: 'You onboard a new client. Your script calls the Uptrack API to create monitors for their website, email server, and VPN. A status page is generated automatically. The on-call tech gets assigned. When something breaks at 3am, the right person is paged — and the client sees real-time status updates.' }}
      cta={{ heading: '30-second checks on every plan', body: 'Start free, move to Pro, and scale to 1,000 monitors on Business.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard">
          <UptimeBarsPiece />
        </ProductFrame>
      }
    />
  )
}
